import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Shield, Users, CheckCircle2, LogOut } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { getPortalToken } from "@/lib/portalSession";
import { toast } from "@/lib/toast";

/**
 * Entry page + session switcher.
 * - Shows which spaces are currently authenticated (admin / client / both / none).
 * - Allows disconnecting one space without affecting the other.
 * - Neutral, visible only when at least one session is active.
 */

function SessionSwitcher() {
  const { user, portalUser, loading, logoutAdmin, logoutPortal } = useAuth();
  const [tick, setTick] = useState(0); // to re-read localStorage after logout

  // Detect tokens directly from storage to stay accurate after scoped logout
  const adminToken = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const portalToken = typeof window !== "undefined" ? getPortalToken() : null;
  const portalStored = typeof window !== "undefined" ? localStorage.getItem("portalUser") : null;

  const adminActive = !!(user || adminToken);
  const portalActive = !!(portalUser || portalToken);

  useEffect(() => {
    // Cross-tab: react to localStorage changes from other tabs
    const handler = () => setTick(t => t + 1);
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  if (loading) return null;
  if (!adminActive && !portalActive) return null;

  const disconnectAdmin = () => {
    logoutAdmin();
    toast.success("Espace gestion deconnecte");
    setTick(t => t + 1);
  };

  const disconnectPortal = () => {
    logoutPortal();
    toast.success("Espace client deconnecte");
    setTick(t => t + 1);
  };

  // Parse stored client user for display
  let clientName = "";
  try {
    if (portalStored) {
      const parsed = JSON.parse(portalStored);
      clientName = `${parsed.first_name || ""} ${parsed.last_name || ""}`.trim();
    }
  } catch {}

  return (
    <div
      className="mt-6 backdrop-blur-sm bg-white/[0.06] border border-white/10 rounded-xl p-3 space-y-2"
      data-testid="session-switcher"
      key={tick}
    >
      <p className="text-[10px] uppercase tracking-wider text-blue-200/50 font-medium px-1">
        Sessions actives sur ce navigateur
      </p>

      {adminActive && (
        <div
          className="flex items-center gap-2 bg-white/[0.04] rounded-lg px-3 py-2"
          data-testid="session-admin-row"
        >
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-white">Espace gestion</p>
            <p className="text-[10px] text-blue-200/50 truncate">
              {user?.email || user?.name || "Administrateur connecte"}
            </p>
          </div>
          <Link
            to="/admin"
            className="text-[10px] font-medium text-blue-200/80 hover:text-white px-2 py-1 rounded hover:bg-white/10 transition"
            data-testid="session-admin-open"
          >
            Ouvrir
          </Link>
          <button
            onClick={disconnectAdmin}
            className="text-[10px] font-medium text-red-300/80 hover:text-red-200 px-2 py-1 rounded hover:bg-red-500/10 transition flex items-center gap-1"
            data-testid="session-admin-logout"
          >
            <LogOut className="w-3 h-3" />
            Deconnecter
          </button>
        </div>
      )}

      {portalActive && (
        <div
          className="flex items-center gap-2 bg-white/[0.04] rounded-lg px-3 py-2"
          data-testid="session-portal-row"
        >
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-white">Espace client</p>
            <p className="text-[10px] text-blue-200/50 truncate">
              {clientName || "Client connecte"}
            </p>
          </div>
          <Link
            to="/portal/dashboard"
            className="text-[10px] font-medium text-blue-200/80 hover:text-white px-2 py-1 rounded hover:bg-white/10 transition"
            data-testid="session-portal-open"
          >
            Ouvrir
          </Link>
          <button
            onClick={disconnectPortal}
            className="text-[10px] font-medium text-red-300/80 hover:text-red-200 px-2 py-1 rounded hover:bg-red-500/10 transition flex items-center gap-1"
            data-testid="session-portal-logout"
          >
            <LogOut className="w-3 h-3" />
            Deconnecter
          </button>
        </div>
      )}
    </div>
  );
}

export default function EntryPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#072354] via-[#0a3270] to-[#143d75] flex items-center justify-center p-4" data-testid="entry-page">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex justify-center mb-10">
          <img
            src="/dg-logo.png"
            alt="DG Fiscal Flow"
            className="w-32 h-32 sm:w-40 sm:h-40 object-contain drop-shadow-[0_0_40px_rgba(255,255,255,0.1)]"
          />
        </div>

        {/* Title */}
        <div className="text-center mb-10">
          <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">DG Fiscal Flow</h1>
          <p className="text-sm text-blue-200/60 mt-2">Gestion fiscale et comptable</p>
        </div>

        {/* Buttons */}
        <div className="space-y-3">
          <Link
            to="/portal"
            className="flex items-center justify-center gap-3 w-full py-3.5 rounded-xl text-sm font-semibold transition bg-white text-[#072354] hover:bg-blue-50 shadow-lg"
            data-testid="entry-client-btn"
          >
            <Users className="w-5 h-5" />
            Espace client
          </Link>

          <Link
            to="/admin/login"
            className="flex items-center justify-center gap-3 w-full py-3.5 rounded-xl text-sm font-semibold transition bg-white/10 text-white border border-white/20 hover:bg-white/20"
            data-testid="entry-admin-btn"
          >
            <Shield className="w-5 h-5" />
            Espace gestion
          </Link>
        </div>

        {/* Session switcher (visible only if at least one session is active) */}
        <SessionSwitcher />

        {/* Footer */}
        <p className="text-center text-[10px] text-blue-200/40 mt-12 flex items-center justify-center gap-2">
          <Link to="/conditions-utilisation" className="hover:text-white transition-colors" data-testid="entry-legal-terms">Conditions d'utilisation</Link>
          <span>·</span>
          <Link to="/politique-confidentialite" className="hover:text-white transition-colors" data-testid="entry-legal-privacy">Politique de confidentialité</Link>
        </p>
        <p className="text-center text-[10px] text-blue-200/30 mt-2">&copy; {new Date().getFullYear()} DG Comptabilite</p>
      </div>
    </div>
  );
}
